<template>
  <main class='complete mt-40'>
    <loading v-if='isOrderLoading' />
    <div v-else>
      <div
        v-if='!hasOrder'
        class="container mb-80">
        <blockCard>
          <div
            id="invalid-order"
            class='p-24 flex flex-col items-center'>
            <img src="@/assets/img/hotel-img-grey.png" alt="" class='mb-32'>
            <div class="empty-notice flex items-center">
              <i class='owl-status-triangle-warning text-orange text-2xl'></i>
              <p class='text-2xl font-medium text-grey-darkest'>{{ $t('checkNoOrder') }}</p>
            </div>
          </div>
        </blockCard>
      </div>

      <div v-else class="container">
        <div class="order-status flex flex-col items-center mt-40 mb-8">
          <h2 class="text-green-dark font-bold mb-16">{{ $t('agent-pay.title') }}</h2>
          <p class="text-grey-darkest text-sm">
             {{ $t('agent-pay.description') }}
            <a href="#select-payment" class="ml-2">{{ $t('agent-pay.link') }}</a>
          </p>
        </div>

        <!-- template source: complete page / start -->
        <blockCard
          noPadding
          class="order-info">
          <div class='order-info__detail py-24 lg:p-24 border-b-1 border-grey-lighter'>
            <h4 class='mb-24'>{{ $t('orderInformation') }}</h4>

            <div class="order-info__detail__date-participant text-grey-darkest mb-32">
              <div class="order-info__detail__date-participant mb-16">
                <i class="owl-calendar mr-8 text-grey text-xl"></i>
                <span>{{ dateRange }}</span>
                <small class="ml-8 text-grey-dark">( {{ diffDays + 1 }} {{ $tc('day', diffDays + 1) }} {{ diffDays }} {{ $tc('night', diffDays) }} )</small>
              </div>
              <div class="order-info__detail__date-participant">
                <i class="owl-user-group mr-8 text-grey text-xl"></i>
                <span>{{ orderInfo.guest.adult }} {{ $tc('adult', orderInfo.guest.adult) }}</span>
                <span v-if="hotelReceptionConfig.child.isReceive">, {{ orderInfo.guest.children }} {{ $tc('child', orderInfo.guest.children) }}</span>
                <span v-if="hotelReceptionConfig.infant.isReceive">, {{ orderInfo.guest.infant }} {{ $tc('infant', orderInfo.guest.infant) }}</span>
              </div>
            </div>

            <div class="template__info-group mb-24">
              <label>{{ $t('orderNumber') }}</label>
              <span>{{ orderInfo.order_serial }}</span>
            </div>

            <div class="template__info-group mb-24">
              <label>{{ $t('orderNights') }}</label>
              <span>{{ diffDaysCount }} {{ $tc('night', diffDaysCount) }}</span>
            </div>

            <div class="order-info__detail__amounts flex flex-col md:flex-row">
              <div class="template__info-group" v-if="isShowRemainTotal">
                <!-- 尾款 -->
                <label>{{ $t('remainTotal') }}</label>
                <span>{{ orderInfo.currency }} <big class='text-blue font-bold'>{{ remainTotal | numeral }}</big></span>
              </div>

              <template v-if="typeOfPaymentMethod === 'collection'">
                <div class="template__info-group">
                  <label>{{ $t('amountPaid') }}</label>
                  <span>
                    {{ orderInfo.currency }}
                    {{ paidDepositTotal | numeral }}
                  </span>
                </div>
              </template>

              <div class="template__info-group">
                <!-- 總金額 -->
                <label>{{ $t('total') }}</label>
                <div class="flex flex-col md:flex-row">
                  <span>{{ orderInfo.currency }} {{ orderInfo.order_total | numeral }}</span>
                </div>
              </div>
            </div>
            <p v-if="hasExtraTaxFeeConfig" class="text-sm mt-16 text-grey-dark">
              <i class="owl-status-triangle-warning text-orange" />
              {{ $t('room_price_has_tax_included') }} {{ orderInfo.currency }} {{ totalExtraFee | numeral }}
            </p>
          </div>

          <div class='order-info__orderer py-24 lg:p-24'>
            <div class="template__info-group">
              <label>{{ $t('lastName') }}</label>
              <span>{{ orderInfo.lastname }}</span>
            </div>
            <div class="template__info-group">
              <label>{{ $t('firstName') }}</label>
              <span>{{ orderInfo.firstname }}</span>
            </div>
            <div class="template__info-group">
              <label>{{ $t('country') }}</label>
              <span>{{ countryName }}</span>
            </div>
            <div class="template__info-group">
              <label>{{ $t('phone') }}</label>
              <span>{{ orderInfo.phone }}</span>
            </div>
            <div class="template__info-group">
              <label>{{ $t('email') }}</label>
              <span>{{ orderInfo.email }}</span>
            </div>
          </div>
          <div v-if="orderInfo.order_requirements" class="text-grey-darker text-sm py-24 lg:p-24 border-t border-grey-lighter">
            <div class="template__info-group">
                <label>{{ $t('orderRequirements') }}</label>
                <span>{{ orderInfo.order_requirements }}</span>
              </div>
          </div>
        </blockCard>

        <!-- 房型訂單 -->
        <div class="hotel-orders">
          <h4 class='py-24 font-bold'>{{ $t('roomTypeAndOrders') }}</h4>
          <blockCard noPadding>
            <div
              v-for="(room, index) in roomsGroupByRoomName"
              :key="index"
              class="hotel-orders__order py-24 lg:p-24"
            >
            <div v-for="(plan, index) in room" :key="`${plan.name}-${index}`">
              <h4 class='text-grey-darker mb-16'>
                {{ $convertI18nObj(plan.room_name_i18n, plan.room_name) }}
              </h4>
              <div class="hotel-orders__container rounded-sm shadow-md border-1 mb-16">
                <hotelOrder
                  :plan="plan"
                  :orderInfo="orderInfo"
                />
              </div>
            </div>
              <h6 class='mb-16'>{{ $t('cancelPolicy') }}</h6>
              <p class='whitespace-pre-wrap text-grey-dark text-sm leading-normal'>{{ $convertI18nObj(room[0].cancel_policy.content_i18n, room[0].cancel_policy.content | contentParser) }}</p>
            </div>
          </blockCard>
        </div>

        <!-- 旅館資訊 -->
        <blockCard
          class='hotel-info'
          :title="$t('hotelInfo')">
          <div class="template__info-group">
            <label>{{ $t('hotel.name') }}</label>
            <span>{{ $convertI18nObj(orderInfo.hotel.name_i18n, orderInfo.hotel.name) }}</span>
          </div>
          <div class="template__info-group" v-if="this['hotel/hotelDisplaySetting'].phone">
            <label>{{ $t('hotel.phone') }}</label>
            <span>{{ orderInfo.hotel.phone }}</span>
          </div>
          <div class="template__info-group">
            <label>{{ $t('hotel.address') }}</label>
            <span>{{ orderInfo.hotel.address }}</span>
          </div>
          <div class="hotel-info__map">
            <iframe v-if="this['hotel/hotelLocatedBy']" :src="`https://www.google.com/maps/embed/v1/place?q=${encodeURI(this['hotel/hotelLocatedBy'])}&key=${googleMapKey}`" width="100%" height="320" frameborder="0" style="border:0" allowfullscreen></iframe>
          </div>
        </blockCard>
        <!-- complete end -->

        <!-- payment start -->
        <h4 class='py-24 font-bold'>{{ $t('agent-pay.link') }}</h4>
        <!-- 付款方式 -->
        <blockCard
          id="select-payment"
          class='payment-ways'
          :title="$t('paymentType')">
          <div v-if="isOrderAllowPay" class="payment-ways__options flex flex-col flex-wrap leading-loose md:flex-row md:items-center">
            <template v-for="way in activedWays">
              <div class="payment-ways__options__option" v-if="isApplePay(way.type)" :key="way.type" @click="onSelectPaymentWay(way.type)">
                <div class="owl-radio-wrap" :class="{ disabled: isSubmitting }">
                  <label :for="`payment-ways__options__option__${way.type}`">
                    <input type="radio" :id="`payment-ways__options__option__${way.type}`" :name="way.type" :checked="way.type === customer.paymentWay" :disabled="isSubmitting">
                    <div class="owl-radio"></div>
                    <template v-if="paymentWayLogo(way.type)">
                      <img :src="paymentWayLogo(way.type)" :alt="way.type" :title="$t(way.type)" v-tippy>
                    </template>
                    <template v-else>
                      <span class="uppercase" :title="$t(way.type)" v-tippy>{{ way.type.includes('atm') ? $t('atm-transfer') : $t(way.type) }}</span>
                    </template>
                  </label>
                </div>
              </div>
            </template>
          </div>
          <div v-else class="text-orange flex items-center font-bold">
            <i class="owl-status-triangle-warning mr-8"></i>
            <span>{{ $t('payments_not_allowed') }}</span>
          </div>
          <div v-show="isInlineCreditCard" class="orderer-info border border-grey-light my-8 p-16 rounded">
            <div class="form-group w-full sm:w-1/2 sm:pr-24">
              <label class='is-required' for="cardNumber">{{ $t('cardNumber') }}</label>
              <cleave v-model="stripeCard.number"
                :options="cardNumberOptions"
                class="input"
                :class="{'input-error': validation.firstError('stripeCard.number')}"
                placeholder="**** **** **** ****"
                :disabled="isSubmitting">
              </cleave>
              <span
                v-if="validation.firstError('stripeCard.number')"
                class='format-error-notify'>{{ $t('errorMsg.empty') }}</span>
            </div>
            <div class="form-group w-full sm:w-1/2 sm:pr-24">
              <label class='is-required' for="cardExpiryDate">{{ $t('cardExpiryDate') }}</label>
              <cleave v-model="stripeCard.expiryDate"
                :options="expiryDateOptions"
                class="input"
                :class="{'input-error': validation.firstError('stripeCard.expiryDate')}"
                placeholder="MM/YY"
                :disabled="isSubmitting">
              </cleave>
            </div>
            <div class="form-group w-full sm:w-1/2 sm:pr-24">
              <label class='is-required' for="cardCvc">{{ $t('cardCvc') }}</label>
              <cleave v-model="stripeCard.cvc"
                :options="cvcOptions"
                class="input"
                :class="{'input-error': validation.firstError('stripeCard.cvc')}"
                placeholder="***"
                :disabled="isSubmitting">
              </cleave>
            </div>
            <div v-show="customer.paymentWay.includes('paynow')" class="form-group w-full sm:w-1/2 sm:pr-24">
              <span class="text-sm text-grey-dark" v-html="$t('payNowCreditCardNote')"></span>
            </div>
          </div>

          <!-- stripe -->
          <div id="stripe-payment-form" v-show="isStripeFuturePay">
            <div class="form-row">
              <label for="stripe-card-element">Credit or debit card</label>
              <div class="flex">
                <div class="stripe-card-element-input w-full mr-8">
                  <div id="stripe-card-element">
                    <!-- A Stripe Element will be inserted here. -->
                  </div>
                  <div id="stripe-card-errors" role="alert"></div>
                </div>
              </div>
            </div>
          </div>
          <!--  -->
          <div
            v-if="customer.paymentWay === 'atm'"
            class="my-16 flex items-center"
          >
            <i class="owl-status-triangle-warning text-orange text-xl mr-8"></i>
            <p class="text-grey-darkest text-sm">
              {{ $t('paymentNote.atm') }}
            </p>
          </div>
          <div class="payment-agreement__row border-t-1 border-grey-lighter pt-24">
            <div
              class="owl-checkbox-wrap"
              :class="{ error: validation.firstError('isAgreementChecked'), disabled: isSubmitting }">
              <blockCard class="bg-orange-lightest" v-if="isTaiwan && isZhTw">
                <div class="notice flex items-center mb-12">
                  <i class="owl-status-triangle-warning text-orange text-xl mr-8"></i>
                  <span class='text-grey-darkest text-sm'>{{ $t('agentPayNotice.title') }}</span>
                </div>
                <ul class="list-block">
                  <li class='leading-normal text-grey-darkest text-sm whitespace-pre-wrap'>{{ $t('agentPayNotice.payment') }}</li>
                  <li class='leading-normal text-grey-darkest text-sm whitespace-pre-wrap' v-html="$t('agentPayNotice.antiFraud')"></li>
                </ul>
              </blockCard>
              <label for="payment-agreement">
                <input type="checkbox" id="payment-agreement" name="payment-agreement" v-model="isAgreementChecked" :class="{ disabled: isSubmitting }">
                <div class="owl-checkbox"></div>
                <span>{{ $t('agreement') }}</span>
              </label>
            </div>
          </div>
          <div class="payment-agreement__row flex flex-col md:flex-row justify-end">
            <button
              class="btn md:btn-lg btn-solid btn-blue w-full md:w-auto md:px-80"
              :disabled="isSubmitButtonDisabled"
              @click="onSubmitBtnClick">
              <span v-show='!isSubmitting'>{{ $t('sendOrder') }}</span>
              <span v-show='isSubmitting'>{{ $t('processing') }}</span>
            </button>
          </div>
        </blockCard>
        <!-- payment end -->
      </div>
    </div>
  </main>
</template>

<script>
// complete start
import { mapActions, mapGetters, mapMutations } from 'vuex'
import _ from 'lodash'
import loading from '@/components/loading'
import blockCard from '@/components/blockCard'
import hotelOrder from '@/views/Complete/components/hotelOrder'
import countries from '@/assets/countries'
import orderApi from '@/api/order'
import fullLoading from '@/mixins/fullLoading'
import $cookies from 'js-cookie'
// complete end

// payment start
import axios from 'axios'
import SimpleVueValidation from 'simple-vue-validator'
import Cleave from 'vue-cleave-component'
import payment from '@/utils/payment'
import SmoothScroll from 'smooth-scroll'
import hotelsApi from '@/api/hotels'

const Validator = SimpleVueValidation.Validator

/* eslint-disable */
const applepay = new OwlTingApplePay('applePayButton', 'https://www.owlting.com/business/ap/validate_hotel.php?u=')
let applepayToken
/* eslint-enable */
// payment end

export default {
  name: 'AgentPay',
  mixins: [SimpleVueValidation.mixin, fullLoading],
  components: {
    // complete start
    blockCard,
    hotelOrder,
    loading,
    // complete end
    // payment start
    Cleave
    // payment end
  },
  data () {
    return {
      // complete start
      isOrderLoading: true,
      hasOrder: false,
      orderInfo: {},
      roomsGroupByRoomName: {},
      paymentStatus: 'unpaid',
      paymentType: '',
      // complete end

      // payment start
      customer: {
        paymentWay: ''
      },
      stripeCard: {
        number: '',
        expiryDate: '',
        cvc: ''
      },
      cardType: null,
      cardNumberOptions: {
        creditCard: true,
        delimiter: ' ',
        onCreditCardTypeChanged: (type) => {
          this.cardType = type
        }
      },
      expiryDateOptions: {
        date: true,
        datePattern: ['m', 'y']
      },
      cvcOptions: {
        numericOnly: true,
        blocks: [4],
        maxLength: 4
      },
      isAgreementChecked: false,
      showApplePayButton: false,
      isSubmitting: false,
      isOrderAllowPay: false,
      activedWays: [],
      paymentWays: [],
      stripeInstance: null,
      stripeCardElement: null,
      stripeClientSecret: '',
      isStripeCardInfoCompleted: false,
      applePayToken: null,
      sonyPaymentToken: null,
      cancelPaymentWaysToken: null
      // payment end
    }
  },
  validators: {
    // payment start
    'customer.paymentWay' (value) {
      return Validator.value(value).required()
    },
    'stripeCard.number' (value) {
      if (this.isInlineCreditCard) {
        return Validator.value(value).required()
      }
      return false
    },
    'stripeCard.expiryDate' (value) {
      if (this.isInlineCreditCard) {
        return Validator.value(value).required()
      }
      return false
    },
    'stripeCard.cvc' (value) {
      if (this.isInlineCreditCard) {
        return Validator.value(value).required()
      }
      return false
    },
    isAgreementChecked (value) {
      return Validator.custom(function () {
        if (!value) {
          return 'checked required !'
        }
      })
    }
    // payment end
  },
  computed: {
    // complete start
    ...mapGetters([
      'isSuper',
      'hotel/hotelLocatedBy',
      'hotel/hotelDisplaySetting',
      'hotel/hotel',
      'hotel/hotelTrackSetting'
    ]),
    ...mapGetters({
      hotelReceptionConfig: 'hotel/hotelReceptionConfig',
      hasExtraTaxFeeConfig: 'hotel/hasExtraTaxFeeConfig'
    }),
    diffDays: function () {
      return this.$dayjs(this.orderInfo.date_end_at).diff(this.$dayjs(this.orderInfo.date_start_at), 'day')
    },
    googleMapKey: function () {
      return process.env.VUE_APP_GOOGLE_MAP_KEY
    },
    isDeposit: function () {
      return this.orderInfo.order_deposit_total > 0
    },
    remainTotal: function () {
      return this.orderInfo.order_total - this.orderInfo.order_deposit_total
    },
    dateRange: function () {
      return this.$dayjs(this.orderInfo.date_start_at).format('YYYY/MM/DD') + ' - ' + this.$dayjs(this.orderInfo.date_end_at).format('YYYY/MM/DD')
    },
    diffDaysCount: function () {
      return this.$dayjs(this.orderInfo.date_end_at).diff(this.$dayjs(this.orderInfo.date_start_at), 'day')
    },
    countryName: function () {
      const matchedCountry = countries.find(country => country.iso2.toLowerCase() === this.orderInfo.country.toLowerCase())
      return matchedCountry ? matchedCountry.name : '-'
    },
    totalExtraFee: function () {
      return this.orderInfo.items?.reduce((sum, item) => {
        return sum + ((+item?.total) - (+item?.price))
      }, 0)
    },
    isFirstPaymentStatusPaid: function () {
      return this.orderInfo?.first_payment_status === 'paid'
    },
    collectionPaymentMethod () {
      return [
        'applepay',
        'atm',
        'atm-paynow',
        'intella-linepay',
        'intella-wechatpay',
        'paypal',
        'unionpay',
        'molpay',
        'stripe-singlepay',
        'intella-alipay',
        'paynow-creditcard3d',
        'sony'
      ]
    },
    nonCollectionPaymentMethod () {
      return [
        'stripe-futurepay',
        'atm-hotel'
      ]
    },
    typeOfPaymentMethod ({ collectionPaymentMethod, nonCollectionPaymentMethod }) {
      const paymentMethod = this.paymentType
      if (collectionPaymentMethod.includes(paymentMethod)) {
        return 'collection'
      } else if (nonCollectionPaymentMethod.includes(paymentMethod)) {
        return 'non-collection'
      } else {
        return paymentMethod
      }
    },
    isShowRemainTotal () {
      const hasDeposit = this.isDeposit
      const notFailPayAndCancel = this.paymentStatus !== 'fail_pay' && this.paymentStatus !== 'cancel'
      const isCollection = this.typeOfPaymentMethod === 'collection'
      return hasDeposit && notFailPayAndCancel && isCollection
    },
    isShowDepositTotal () {
      const refundStatus = ['partial_refund', 'refund', 'wait_refund']
      return this.isFirstPaymentStatusPaid || refundStatus.includes(this.orderInfo.first_payment_status)
    },
    paidDepositTotal () {
      return this.isShowDepositTotal ? this.orderInfo.order_deposit_total : 0
    },
    // complete end

    // payment start
    ...mapGetters({
      selectedLanguage: 'selectedLanguage',
      stripeKey: 'hotel/stripeKey',
      sonyPaymentPublicKey: 'hotel/sonyPaymentPublicKey'
    }),
    isTaiwan: function () {
      return this['hotel/hotel'].country.toLowerCase() === 'tw'
    },
    isZhTw: function () {
      return this.selectedLanguage.toLowerCase() === 'zh_tw'
    },
    isInlineCreditCard () {
      const payments = ['stripe-singlepay', 'paynow-creditcard', 'paynow-creditcard3d', 'sony']
      return _.includes(payments, this.customer.paymentWay)
    },
    isStripeFuturePay () {
      return this.customer.paymentWay === 'stripe-futurepay'
    },
    isStripeFuturePayValid () {
      return !!this.stripeClientSecret
    },
    isSubmitButtonDisabled () {
      return this.isSubmitting || !this.isOrderAllowPay || (this.isStripeFuturePay && !this.isStripeCardInfoCompleted)
    },
    hasStripeFuturePayment () {
      return this.activedWays.find(p => p.type === 'stripe-futurepay')
    },
    hasSonyPayment () {
      return this.activedWays.some(p => p.type === 'sony')
    },
    errorListForI18n () {
      return {
        '20000': this.$t('creditcardInvalidFormat'),
        '90000': this.$t('unknownError')
      }
    }
    // payment end
  },
  methods: {
    // complete start
    ...mapMutations(['SET_COVER_LOADING']),
    ...mapActions([
      'order/getCompletedOrder',
      'hotel/fetchSonyPaymentPublicKey'
    ]),
    fetchOrder: function () {
      const { hotelId, orderSerial } = this.$route.params
      const params = { hotelId, orderSerial }

      return this['order/getCompletedOrder'](params)
        .then(res => {
          if (res.status === 200) {
            this.orderInfo = res.data.data
            this.paymentStatus = res.data.data.status
            this.paymentType = res.data.data.payment_type

            if (this.paymentStatus !== 'unpaid') {
              this.$router.replace({
                name: 'Complete',
                params: {
                  hotelId: this.$route.params.hotelId,
                  orderSerial: this.$route.params.orderSerial
                }
              })
              return
            }

            if (this.orderInfo.extra_receptions) {
              this.orderInfo.items = this.mapExtraReceptions(this.orderInfo.items, this.orderInfo.extra_receptions)
            }

            this.roomsGroupByRoomName = _.groupBy(this.orderInfo.items, function (room) {
              return room.room_name
            })

            this.hasOrder = true

            const paymentFlagFromCookies = $cookies.get('gtag-payment-flag')

            if (paymentFlagFromCookies === this['hotel/hotel'].hotel_uuid) {
              this.sendGtagPurchase()
              if (this['hotel/hotelTrackSetting'].conversion_ids) {
                this.sendGtagConversion()
              }
              $cookies.remove('gtag-payment-flag')
            }
          }
        }).catch(err => {
          console.debug('getCompletedOrder', err)
          this.hasOrder = false
        }).finally(() => {
          this.isOrderLoading = false
        })
    },
    formatProductInEcommerce () {
      return this.orderInfo.items.map(item => {
        const roomName = this.$convertI18nObj(item.room_name_i18n, item.room_name)
        const planName = this.$convertI18nObj(item.plan_name_i18n, item.plan_name)
        return {
          id: `${this['hotel/hotel'].hotel_uuid}_${item.product_id}`,
          name: `${this['hotel/hotel'].name}_${roomName}_${planName}`,
          price: item.price, // 未稅價
          quantity: item.qty,
          category: 'hotel',
          brand: this['hotel/hotel'].name
        }
      })
    },
    sendGtagPurchase () {
      const purchaseData = {
        transaction_id: this.orderInfo.order_serial,
        value: `${this.orderInfo.order_total - this.totalExtraFee}`,
        tax: this.totalExtraFee,
        currency: this.orderInfo.currency,
        affiliation: this['hotel/hotel'].name,
        items: this.formatProductInEcommerce(),
        coupon: this.orderInfo.coupons.length > 0 ? this.orderInfo.coupons[0].name : ''
      }
      this.$gtag.event('purchase', purchaseData)
    },
    sendGtagConversion () {
      const conversionData = {
        value: `${this.orderInfo.order_total - this.totalExtraFee}`,
        currency: this.orderInfo.currency,
        transaction_id: this.orderInfo.order_serial,
        send_to: _.last(this['hotel/hotelTrackSetting']?.conversion_ids)
      }
      this.$gtag.event('conversion', conversionData)
    },
    mapExtraReceptions (orderItem, extraReceptions) {
      extraReceptions.forEach(receptions => {
        receptions.extra_persons.forEach(extraDetail => {
          const orderItemCandidates = orderItem.filter(item => {
            const [roomId, planId] = item.product_id.split('_')
            return receptions.plan_id === +planId && receptions.room_id === +roomId && extraDetail.date === item.date
          })

          if (orderItemCandidates.length > 0) {
            const notHaveExtraPersonsItem = orderItemCandidates.find(item => !item.extra_persons)
            if (notHaveExtraPersonsItem) {
              notHaveExtraPersonsItem.extra_persons = extraDetail
            }
          }
        })
      })
      return orderItem
    },
    // complete end

    // payment start
    fetchPaymentWaysForOrder () {
      const startDate = this.$dayjs(this.orderInfo.date_start_at).format('YYYY-MM-DD')
      const endDate = this.$dayjs(this.orderInfo.date_end_at).format('YYYY-MM-DD')
      const hotelUuid = this['hotel/hotel'].hotel_uuid

      if (this.cancelPaymentWaysToken) this.cancelPaymentWaysToken.cancel()
      this.cancelPaymentWaysToken = axios.CancelToken.source()
      hotelsApi.fetchPaymentWays(hotelUuid, startDate, endDate, this.cancelPaymentWaysToken)
        .then(res => {
          const { payment_ways: paymentWays } = res.data?.data || {}
          this.paymentWays = Object.entries(paymentWays || {}).reduce((acc, [type, active]) => {
            acc.push({ type, active })
            return acc
          }, [])
          this.getActivedWays()
          this.initStripe()

          if (this.hasSonyPayment) {
            this['hotel/fetchSonyPaymentPublicKey']()
            this.fetchSonyPaymentScript()
          }
        })
    },
    getActivedWays () {
      const activatedPayments = this.paymentWays
      this.isOrderAllowPay = activatedPayments.length > 0
      this.activedWays = activatedPayments
      if (this.activedWays.length > 0) {
        this.customer.paymentWay = this.activedWays[0].type !== 'applepay'
          ? this.activedWays[0].type
          : this.showApplePayButton
            ? this.activedWays[0].type
            : this.activedWays[1]?.type || ''
      }
    },
    isApplePay (type) {
      if (type === 'applepay') {
        return this.showApplePayButton
      }
      return true
    },
    isApplePaySupport () {
      /* eslint-disable */
      if (applepay.isCanMakePayment()) {
        this.showApplePayButton = true
      }
      const that = this
      applepay.event_SendApplepay = function (token) {
        that.applePayToken = token
        that.submitForm()
      }
      applepay.event_CancelApplepay = function () {
        that.handleSubmitStatus(false)
        console.debug('[ApplePay] Cancelled')
      }
      /* eslint-enable */
    },
    paymentWayLogo (type) {
      for (const format of ['png', 'jpg', 'svg']) {
        try {
          return require(`@/assets/img/payment-logo/logo-${type}.${format}`)
        } catch (err) {
          // console.debug('paymentWayLogo', err)
        }
      }

      return false
    },
    onSelectPaymentWay (way) {
      this.customer.paymentWay = way
    },
    handleSubmitStatus (bool = false) {
      this.isSubmitting = bool
      if (this.stripeCardElement) this.stripeCardElement.update({ disabled: bool })
    },
    submitForm () {
      this.handleSubmitStatus(true)

      const data = {
        payment_name: this.customer.paymentWay,
        card: {
          number: this.stripeCard.number,
          year: '20' + this.stripeCard.expiryDate.slice(2, 4),
          month: this.stripeCard.expiryDate.slice(0, 2),
          cvc: this.stripeCard.cvc
        },
        stripe_public_key: this.stripeKey
      }

      if (this.sonyPaymentToken) {
        data.sony_payment_token = this.sonyPaymentToken
      }

      /* eslint-disable */
      if (data.payment_name === 'applepay') {
        data.payment_token = JSON.stringify(this.applePayToken?.paymentData)
      }
      /* eslint-enable */

      const { hotel_uuid: hotelUuid } = this['hotel/hotel']
      const orderSerial = this.orderInfo.order_serial

      orderApi.payAgentOrder(hotelUuid, orderSerial, data)
        .then(res => {
          if (res.status === 200) {
            if (res.data.status === 0) {
              /* eslint-disable */
              if (this.customer.paymentWay === 'applepay') {
                applepay.completePaymentSessionSuccess()
              }
              /* eslint-enable */
              if (!this.validateLinepayPayload(res.data.data)) {
                this.handleSubmitStatus(false)
                this.handleError('payFailed')
              } else {
                payment(res.data.data)
                  .then(clientSecret => {
                    switch (res.data.data.payment_type) {
                      case 'linepay': {
                        const url = res.data.data?.payment_payload?.data?.url
                        if (url) {
                          location.href = url
                        } else {
                          this.handleError('payFailed')
                          this.handleSubmitStatus(false)
                        }
                      } break
                      default:
                        break
                    }
                    if (this.isStripeFuturePay && clientSecret) {
                      this.stripeClientSecret = clientSecret
                      this.onSubmitStripeFuturePayCard()
                    } else {
                      this.handleSubmitStatus(false)
                    }
                  })
                  .catch(error => {
                    this.handleSubmitStatus(false)
                    this.handleError(error.message)
                  })
              }
            } else {
              this.handleSubmitStatus(false)
              /* eslint-disable */
              if (this.customer.paymentWay === 'applepay') {
                applepay.completePaymentSessionFail()
              }
              /* eslint-enable */
              if (Object.keys(this.errorListForI18n).includes(res.data.status.toString())) {
                return this.handleError(res.data.status.toString())
              }
              this.handleError(res.data.msg)
            }
          } else {
            this.handleSubmitStatus(false)
            /* eslint-disable */
            if (this.customer.paymentWay === 'applepay') {
              applepay.completePaymentSessionFail()
            }
            /* eslint-enable */
            this.handleError(res.data.msg)
          }
        }).catch(err => {
          this.handleError(err.message)
          this.handleSubmitStatus(false)
        })
    },
    doApplePay () {
      this.handleSubmitStatus(true)
      const integerCurrency = ['TWD', 'JPY']
      const isIntegerCurrencyHotel = integerCurrency.includes(this['hotel/hotel'].currency)
      const orderTotal = this.isDeposit ? this.remainTotal : this.orderInfo.order_total
      const total = isIntegerCurrencyHotel
        ? this.$numeral(Math.round(orderTotal)).value()
        : this.$numeral(orderTotal).value()
      /* eslint-disable */
      const result = applepay.sendIfTokenValid(applepayToken, this['hotel/hotel'].currency, _.toUpper(this['hotel/hotel'].country), this['hotel/hotel'].name, total)
      console.debug('[APPLE PAY] result', result)
      /* eslint-enable */
    },
    onSubmitBtnClick: _.throttle(function () {
      if (!this.isOrderAllowPay) return false
      if (this.isStripeFuturePay && !this.isStripeCardInfoCompleted) return false
      this.$validate()
        .then(success => {
          if (success) {
            switch (this.customer.paymentWay) {
              case 'applepay':
                this.doApplePay()
                break
              case 'sony':
                this.doSonyPay()
                break
              default:
                this.submitForm()
                break
            }
          } else {
            const scroll = new SmoothScroll()
            const errorNode = document.querySelector('.input-error') || document.querySelector('.selector-error')
            scroll.animateScroll(errorNode, {}, { offset: 50 })
            return false
          }
        })
    }, 800),
    initStripe () {
      if (!this.hasStripeFuturePayment || !this.stripeKey) return
      this.$nextTick(() => {
        /* eslint-disable */
        this.stripeInstance = Stripe(this.stripeKey)
        /* eslint-enable */
        const stripeLocaleMap = { en: 'en', ja: 'ja', zh_TW: 'zh-TW' }
        const elements = this.stripeInstance.elements({
          locale: stripeLocaleMap[this.selectedLanguage] || 'auto'
        })

        this.stripeCardElement = elements.create('card', {
          iconStyle: 'solid',
          style: {
            base: {
              fontWeight: 400,
              fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
              fontSize: '1rem',
              fontSmoothing: 'antialiased',
              '::placeholder': { color: '#5C656B' },
              ':-webkit-autofill': { color: '#078ABC' }
            },
            invalid: {
              iconColor: '#EA5E2E',
              color: '#EA5E2E'
            }
          }
        })

        setTimeout(() => {
          this.stripeCardElement.mount('#stripe-card-element')
        }, 100)

        this.stripeCardElement.on('change', evt => {
          this.isStripeCardInfoCompleted = evt.complete
        })
      })
    },
    async onSubmitStripeFuturePayCard () {
      const { setupIntent, error } = await this.stripeInstance.confirmCardSetup(
        this.stripeClientSecret,
        {
          payment_method: {
            card: this.stripeCardElement,
            billing_details: {}
          }
        }
      )

      this.stripeClientSecret = ''
      if (error) {
        console.error('STRIPE CONFIRM CARD ERROR: ', error)
        this.handleError(this.$t('stripeErrorButOrderCreated'), this.goOrderCompletePage)
        this.handleSubmitStatus(false)
      } else {
        this.handleSubmitStatus(false)
        if (setupIntent.status === 'succeeded') {
          this.goOrderCompletePage()
        }
      }
    },
    goOrderCompletePage () {
      this.$router.push({
        name: 'Complete',
        params: {
          hotelId: this.$route.params.hotelId,
          orderSerial: this.orderInfo.order_serial
        }
      })
    },
    validateLinepayPayload (data) {
      // eslint-disable-next-line
      return data.payment_type !== 'intella-linepay' || (data.payment_payload?.Data?.qrcode_url && data.payment_payload?.Data?.urlToken)
    },
    doSonyPay () {
      this.handleSubmitStatus(true)
      const rapp = window.rappJs(this.sonyPaymentPublicKey)
      Promise.race([
        rapp.createToken({
          card: {
            card_number: this.stripeCard.number,
            exp_year: this.stripeCard.expiryDate.substring(2),
            exp_month: this.stripeCard.expiryDate.substring(0, 2),
            cvv: this.stripeCard.cvc
          }
        }),
        new Promise((resolve, reject) => {
          setTimeout(() => { reject(new Error('Timeout')) }, 1000 * 10)
        })
      ]).then(tokenInfo => {
        this.sonyPaymentToken = tokenInfo.token
        this.submitForm()
      }).catch(() => {
        this.handleSubmitStatus(false)
        this.handleError('payFailed')
      })
    },
    fetchSonyPaymentScript () {
      if (!document.getElementById('rapp')) {
        const scriptTag = document.createElement('script')
        scriptTag.src = process.env.VUE_APP_SONY_PAYMENT_TOKEN_JS
        scriptTag.id = 'rapp'
        document.getElementsByTagName('head')[0].appendChild(scriptTag)
      }
    },
    handleError (msg, cb) {
      this.$modal.show('dialog', {
        title: `<i class="owl-status-triangle-warning text-xl text-orange"></i> <span class="text-xl text-orange">Payment Error</span>`,
        text: this.getErrorMsg(msg),
        buttons: [
          {
            title: this.$t('close'),
            handler: () => {
              this.$modal.hide('dialog')
              this.handleSubmitStatus(false)
              if (cb && typeof cb === 'function') cb()
            }
          }
        ]
      })
    },
    getErrorMsg (msg) {
      if (this.errorListForI18n[msg]) {
        return this.errorListForI18n[msg]
      }
      console.error('ERROR MESSAGE NOT FOUND: ', msg)
      return this.$t('paymentErrorDefault')
    }
    // payment end
  },
  activated () {
    this.fetchOrder().then(() => {
      if (this.hasOrder) {
        this.fetchPaymentWaysForOrder()
      }
    })
    this.isApplePaySupport()
    this.isSubmitting = false
  },
  deactivated () {
    if (!this.isSuper) {
      window.location.reload()
    }
  },
  watch: {
    // payment start
    'customer.paymentWay': {
      handler (value) {
        if (value === 'taiwan-travel') {
          const content = '您在本官網下單所屬的消費類別為「其他」，不屬於國旅卡的觀光旅遊額度使用範圍，將列入「自行運用額度」核銷。請確認您仍有可用的「其他類別」額度再進行刷卡，以免影響補助申請，謝謝。'
          this.$modal.show('dialog', {
            text: `<div class="p-16 text-sm text-grey-darker leading-normal whitespace-pre-wrap">${content}</div>`,
            buttons: [{ title: this.$t('close') }]
          })
        }
      },
      deep: true,
      immediate: true
    },
    isSubmitting (bool) {
      this.SET_COVER_LOADING(bool)
    }
    // payment end
  }
}
</script>

<style lang="scss">
/* payment start */
.bg-orange-lightest {
  .block-card {
    margin-bottom: 0;
  }
}
/* payment end */
</style>

<style lang="sass" scoped>
// complete start
.template__info-group
  @apply flex flex-col
  label
    @apply text-base text-grey-darkest mb-8
  span
    @apply text-sm text-grey-dark
  &:not(:last-of-type)
    @apply mb-24

.order-info
  &__detail
    &__amounts
      .template__info-group
        &:not(:last-of-type)
          @apply mb-24
        @screen md
          @apply flex-1 mb-0 #{!important}
          &:not(:first-of-type)
            @apply pl-24
          &:not(:last-of-type)
            @apply border-r-1 border-grey-lighter

.hotel-orders
  border-color: #f2f4f6
  &__order
    &:not(:last-of-type)
      @apply border-b-1 border-grey-lighter
// complete end

// payment start
.payment-ways
  &__options__option
    @apply flex items-center cursor-pointer
    input
      @apply mr-12
    label
      @apply cursor-pointer
    &:not(:last-of-type)
      @apply mb-24
      @screen md
        @apply mr-40 mb-0
    img
      height: 24px

.payment-agreement__row
  @apply items-center mt-24
  input
    @apply mr-16

.orderer-info
  .form-group
    @apply relative
    &:not(:last-of-type)
      @apply mb-24
    label
      @apply block text-grey-darker mb-16
      &.is-required
        &::before
          @apply text-red
          content: '* '
    .format-error-notify
      @apply mt-4 block text-sm text-red

.list-block
  padding-inline-start: 28px
ul
  list-style: none
li
  margin-bottom: 8px
ul li::before
  content: "\2022"
  color: #EA5E2E
  font-weight: bold
  display: inline-block
  width: 1em
  margin-left: -1em
// payment end
</style>

<style lang="sass">
// payment start
#stripe-payment-form
  @apply bg-grey-lightest rounded p-16 my-16
  label[for=stripe-card-element]
    @apply block mb-12 text-grey-darkest font-bold
  .StripeElement
    box-sizing: border-box
    height: 40px
    padding: 10px 12px
    border: 1px solid transparent
    border-radius: 4px
    background-color: white
    box-shadow: 0 1px 3px 0 #e6ebf1
    -webkit-transition: box-shadow 150ms ease
    transition: box-shadow 150ms ease
  #stripe-card-element
    @apply bg-white shadow
  .StripeElement--invalid
    @apply border-red
  .StripeElement--webkit-autofill
    background-color: #fefde5 !important
  .__PrivateStripeElement
    height: 40px
    overflow: hidden
// payment end
</style>
